import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, Subscription } from 'rxjs';
import { UploadService } from './upload.service';
import { UploadHistoryStore } from './upload-history.store';
import { FileBrowserStore } from './file-browser.store';
import { FilesystemFilterService } from './filesystem-filter.service';
import { MessageService } from 'primeng/api';
import type { FileWithPath } from '../models/upload.model';
import type { QueuedBatch, RejectedFile } from '../models/upload-queue.model';

@Injectable()
export class UploadQueueStore {
  private readonly uploadService = inject(UploadService);
  private readonly filesystemFilter = inject(FilesystemFilterService);
  private readonly messageService = inject(MessageService);
  private readonly uploadHistory = inject(UploadHistoryStore);
  private readonly browserStore = inject(FileBrowserStore);
  private readonly destroyRef = inject(DestroyRef);

  private queue: QueuedBatch[] = [];
  private inFlight = false;
  private uploadSubscription?: Subscription;

  readonly batchComplete = new Subject<void>();

  async handleDroppedItems(targetPath: string, items: DataTransferItemList): Promise<void> {
    this.browserStore.setLoading(true);
    let files: FileWithPath[] = [];
    let failed: string[] = [];
    try {
      ({ files, failed } = await this.uploadService.collectFromDataTransfer(items));
    } catch (err) {
      console.error('Failed to read dropped items', err);
      this.messageService.add({
        severity: 'error',
        summary: 'A fájlok beolvasása sikertelen',
        detail: 'A behúzott elemeket nem sikerült beolvasni. Próbáld újra.',
        life: 6000,
      });
      this.browserStore.setLoading(false);
      return;
    }

    if (failed.length > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: `${failed.length} fájl nem olvasható`,
        detail: failed.join(', '),
        life: 6000,
      });
    }

    if (files.length > 0) {
      this.enqueue(targetPath, files);
    } else {
      this.browserStore.setLoading(false);
    }
  }

  enqueue(targetPath: string, files: FileWithPath[]): void {
    const { accepted, rejected } = this.partitionByValidation(files);

    if (rejected.length > 0) {
      this.notifyRejected(rejected);
    }

    if (accepted.length === 0) {
      if (!this.inFlight && this.queue.length === 0) {
        this.browserStore.setLoading(false);
      }
      return;
    }

    this.queue.push({ files: accepted, targetPath });
    this.processNext();
  }

  clear(): void {
    this.queue = [];
  }

  private partitionByValidation(files: FileWithPath[]): {
    accepted: FileWithPath[];
    rejected: RejectedFile[];
  } {
    const accepted: FileWithPath[] = [];
    const rejected: RejectedFile[] = [];
    for (const item of files) {
      const result = this.filesystemFilter.validateFile(item.file);
      if (result.ok) {
        accepted.push(item);
      } else {
        rejected.push({ file: item, result });
      }
    }
    return { accepted, rejected };
  }

  private notifyRejected(rejected: RejectedFile[]): void {
    let formatCount = 0;
    let sizeCount = 0;
    for (const r of rejected) {
      if (r.result.reason === 'size') sizeCount++;
      else formatCount++;
    }
    const parts: string[] = [];
    if (formatCount > 0) parts.push(`${formatCount} nem támogatott formátum`);
    if (sizeCount > 0) parts.push(`${sizeCount} méretkorlát túllépve`);
    const detail = rejected.map((r) => r.file.file.name).join(', ');
    this.messageService.add({
      severity: 'warn',
      summary: `${rejected.length} fájl kihagyva: ${parts.join(', ')}`,
      detail,
      life: 6000,
    });
  }

  private processNext(): void {
    if (this.inFlight || this.queue.length === 0) {
      return;
    }
    const { files, targetPath } = this.queue.shift()!;
    this.startBatch(targetPath, files);
  }

  private startBatch(targetPath: string, files: FileWithPath[]): void {
    this.inFlight = true;
    this.uploadHistory.progress.set(0);
    if (!this.uploadHistory.visible()) {
      this.uploadHistory.expanded.set(true);
    }
    this.uploadHistory.visible.set(true);
    this.browserStore.setLoading(true);

    this.uploadSubscription = this.uploadService
      .uploadFilesSequentially(targetPath, files)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (event) => {
          switch (event.kind) {
            case 'init':
              this.uploadHistory.beginBatch(event.groups);
              break;
            case 'progress':
              this.uploadHistory.progress.set(event.overall);
              break;
            case 'group':
              this.uploadHistory.updateGroup(event.index, event.patch);
              break;
            case 'child':
              this.uploadHistory.updateChild(event.groupIndex, event.childIndex, event.patch);
              break;
            case 'all-done':
              this.batchComplete.next();
              this.inFlight = false;
              if (this.queue.length === 0) {
                this.browserStore.setLoading(false);
              }
              this.processNext();
              break;
          }
        },
        error: (err) => {
          console.error('Upload batch failed', err);
          this.inFlight = false;
          if (this.queue.length === 0) {
            this.browserStore.setLoading(false);
          }
          this.processNext();
        },
      });
  }
}
